/**
 * In-app install banner — the missing half of the PWA work.
 *
 * The manifest and the service worker only make the app *installable*; no
 * browser reliably renders an install CTA on its own any more:
 *
 *  - Chromium (Android + desktop) fires `beforeinstallprompt` and shows, at
 *    most, a mini-infobar that is heavily throttled — dismissed once and it
 *    stays quiet for ~90 days. The event must be caught and replayed from our
 *    own button for the offer to be dependable.
 *  - iOS Safari has NO install API at all. Nothing fires, nothing can be
 *    prompted; the only route is Share → Add to Home Screen, so all we can do
 *    is point at it.
 *  - macOS Safari 17+ installs via the menu bar (File → Add to Dock), likewise
 *    with no programmatic hook.
 *
 * So: one banner, three modes. Vanilla TS rather than an SFC because the Vue
 * app only mounts on `/play` — the offer has to survive the login screen too.
 */
import { t } from './i18n';

/** Dismissal stamp. Same `fw:` namespace as the idle clock; copy is not secret. */
const DISMISS_KEY = 'fw:install-dismissed-at';
/** Nag at most once a week — a declined offer that returns tomorrow is spam. */
export const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/** Chromium-only event; not in lib.dom. */
export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

/**
 * Every browser that can install this app, and how it does it:
 *
 * 'native'   — Chromium (Android Chrome, Samsung Internet, desktop Chrome/Edge
 *              on Windows, macOS, Linux, ChromeOS): the event fires, one tap.
 * 'ios'      — iOS/iPadOS Safari: no install API, Share → Add to Home Screen.
 * 'iosMenu'  — Chrome/Edge/Firefox on iOS: also WebKit, also no API, but the
 *              Share sheet lives behind their own menu button, not the toolbar.
 * 'mac'      — macOS Safari 17+: File → Add to Dock, no API either.
 * 'firefox'  — Firefox on Android: installs from the ⋮ menu, and it is the one
 *              mobile browser that supports installing without firing anything.
 *
 * Desktop Firefox and Safari before 17 cannot install a web app at all — they
 * get no banner, because a hint pointing at a menu item they do not have is
 * worse than silence.
 */
export type InstallMode = 'native' | 'ios' | 'iosMenu' | 'mac' | 'firefox';

type Env = {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
};

/** iPadOS 13+ reports itself as a Mac; the touch count is what tells them apart. */
export function isIos(env: Env): boolean {
  return /iPhone|iPad|iPod/.test(env.userAgent)
    || (env.platform === 'MacIntel' && env.maxTouchPoints > 1);
}

/** Safari's own version, or 0 when this is not Safari. */
function safariVersion(ua: string): number {
  if (!/Safari\//.test(ua) || /Chrome|Chromium|Edg\/|OPR\/|Firefox/.test(ua)) return 0;
  return Number(/Version\/(\d+)/.exec(ua)?.[1] ?? 0);
}

/**
 * Real Safari on a real Mac, new enough to install. Chrome, Edge, Opera and
 * Firefox on macOS all carry "Safari" in their UA, so they are excluded by
 * name; iPads are caught above. "Add to Dock" arrived in Safari 17 — pointing
 * an older Safari at a menu item it does not have would be a lie.
 */
export function isMacSafari(env: Env): boolean {
  return env.platform === 'MacIntel'
    && env.maxTouchPoints <= 1
    && safariVersion(env.userAgent) >= 17;
}

/** Chrome/Edge/Firefox on iOS — WebKit underneath, own menu on top. */
function isIosNonSafari(ua: string): boolean {
  return /CriOS|FxiOS|EdgiOS|OPiOS|GSA\//.test(ua);
}

/** Firefox on Android: installs from its menu, never fires the event. */
function isAndroidFirefox(ua: string): boolean {
  return /Android/.test(ua) && /Firefox\/|FxiOS/.test(ua);
}

/** The mode that needs manual instructions, or null where the event will fire. */
export function manualMode(env: Env): InstallMode | null {
  if (isIos(env)) return isIosNonSafari(env.userAgent) ? 'iosMenu' : 'ios';
  if (isMacSafari(env)) return 'mac';
  if (isAndroidFirefox(env.userAgent)) return 'firefox';
  return null;
}

/** Already installed? Then there is nothing to offer. */
export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches === true
    // Safari's non-standard flag, true only when launched from the home screen.
    || (navigator as unknown as { standalone?: boolean }).standalone === true;
}

export function recentlyDismissed(now: number = Date.now()): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return Number.isFinite(at) && at > 0 && now - at < DISMISS_COOLDOWN_MS;
  } catch {
    return false; // private mode — no memory, so nothing to honour
  }
}

export function rememberDismissal(now: number = Date.now()): void {
  try { localStorage.setItem(DISMISS_KEY, String(now)); } catch { /* banner just returns next visit */ }
}

/** The Share glyph iOS puts in its toolbar — drawn, because we cannot link it. */
const SHARE_GLYPH = `<svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" class="inline-block h-3.5 w-3.5 align-[-2px]"><path d="M8 1l3 3h-2v6H7V4H5l3-3zM3 7v7h10V7h-2V5.5h3.5v10h-13v-10H5V7H3z"/></svg>`;

function buildBanner(mode: InstallMode, onInstall: () => void, onDismiss: () => void): HTMLElement {
  const host = document.createElement('div');
  // z-54: above ordinary page chrome, deliberately BELOW the queue dock (55)
  // and every overlay (200+) — an install offer must never bury live gameplay.
  host.className = 'fixed inset-x-0 bottom-0 z-[54] p-3 sm:left-auto sm:right-4 sm:w-[26rem]';
  host.style.paddingBottom = 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-label', t('install.title'));

  // The hint carries markup (the Share glyph, emphasised menu words), so it is
  // assembled from our own strings only — never from anything user-supplied.
  const strong = (s: string) => `<span class="font-semibold text-text">${s}</span>`;
  const share = `<span class="whitespace-nowrap font-semibold text-text">${SHARE_GLYPH} ${t('install.shareLabel')}</span>`;
  const HINTS: Record<InstallMode, () => string> = {
    native: () => t('install.hintNative'),
    ios: () => t('install.hintIos', { share, add: strong(t('install.addLabel')) }),
    iosMenu: () => t('install.hintIosMenu', { share, add: strong(t('install.addLabel')) }),
    mac: () => t('install.hintMac', { file: strong(t('install.fileLabel')), add: strong(t('install.dockLabel')) }),
    firefox: () => t('install.hintFirefox', { add: strong(t('install.cta')) }),
  };
  const hint = HINTS[mode]();

  host.innerHTML = `
    <div class="flex items-center gap-3 rounded-xl border border-border bg-surface/95 p-3 shadow-[0_10px_30px_rgba(0,0,0,.55)] backdrop-blur">
      <img src="/icon-192.png" alt="" width="48" height="48" class="h-12 w-12 shrink-0 rounded-lg border border-border/60" />
      <div class="min-w-0 flex-1">
        <p class="font-label text-sm font-bold tracking-wide text-gold">${t('install.title')}</p>
        <p class="mt-0.5 text-xs leading-snug text-text-dim">${hint}</p>
      </div>
      ${mode === 'native'
        ? `<button type="button" data-act="install" class="min-h-11 shrink-0 rounded-lg bg-gold px-4 font-label text-sm font-bold tracking-wide text-bg transition hover:bg-gold-bright">${t('install.cta')}</button>`
        : ''}
      <button type="button" data-act="dismiss" aria-label="${t('install.dismiss')}" class="grid min-h-11 min-w-11 shrink-0 place-items-center text-text-dim transition hover:text-text">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" class="h-4 w-4"><path d="M4 4l8 8M12 4l-8 8"/></svg>
      </button>
    </div>`;

  host.querySelector('[data-act="install"]')?.addEventListener('click', onInstall);
  host.querySelector('[data-act="dismiss"]')?.addEventListener('click', onDismiss);
  return host;
}

/**
 * Arm the install offer. Safe to call on every boot — it shows nothing when the
 * app already runs standalone, when the offer was dismissed this week, or when
 * the browser never signals installability.
 *
 * Returns a teardown that removes the banner and every listener.
 */
export function installPrompt(root: HTMLElement = document.body): () => void {
  let deferred: BeforeInstallPromptEvent | null = null;
  let banner: HTMLElement | null = null;

  const hide = () => { banner?.remove(); banner = null; };

  const dismiss = () => { hide(); rememberDismissal(); };

  const show = (mode: InstallMode) => {
    if (banner || isStandalone() || recentlyDismissed()) return;
    banner = buildBanner(mode, install, dismiss);
    root.appendChild(banner);
  };

  async function install(): Promise<void> {
    const evt = deferred;
    if (!evt) return;
    deferred = null;
    hide();
    await evt.prompt();
    const { outcome } = await evt.userChoice;
    // Accepted → `appinstalled` follows and there is nothing left to offer.
    // Declined → stop asking for a week.
    if (outcome === 'dismissed') rememberDismissal();
  }

  const onBeforeInstallPrompt = (e: Event) => {
    e.preventDefault(); // suppress Chrome's own throttled infobar; ours replaces it
    deferred = e as BeforeInstallPromptEvent;
    show('native');
  };

  const onInstalled = () => { deferred = null; hide(); };

  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onInstalled);

  // Safari fires neither event, so its hint is decided from the platform alone.
  const manual = manualMode({
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
  });
  if (manual) show(manual);

  return () => {
    window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', onInstalled);
    hide();
  };
}
